import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { AcademyOnlyRoute } from './contexts/CalibrationContext';
import { Home } from './pages/Home';
import { Onboarding } from './pages/Onboarding';
import { Sessions } from './pages/Sessions';
import { SessionDetail } from './pages/SessionDetail';
import { SessionRun } from './pages/SessionRun';
const SessionTactics3D = lazy(() =>
  import('./pages/SessionTactics3D').then((m) => ({ default: m.SessionTactics3D })),
);
const SessionTactics2D = lazy(() =>
  import('./pages/SessionTactics2D').then((m) => ({ default: m.SessionTactics2D })),
);
import { SessionNew } from './pages/SessionNew';
import { Drills } from './pages/Drills';
import { DrillDetail } from './pages/DrillDetail';
import { DrillNew } from './pages/DrillNew';
import { Tactics } from './pages/Tactics';
import { DrillCanvasPage } from './pages/DrillCanvas';
import { Calendar } from './pages/Calendar';
import { Players } from './pages/Players';
import { PlayerDetail } from './pages/PlayerDetail';
import { Reviews } from './pages/Reviews';
import { CoachGrowthCheckIn } from './pages/CoachGrowthCheckIn';
import { Principles } from './pages/Principles';
import { Curriculum } from './pages/Curriculum';
import { Season } from './pages/Season';
import { Games } from './pages/Games';
import { GameDay } from './pages/GameDay';
import { Memories } from './pages/Memories';
import { Clips } from './pages/Clips';
import { Settings } from './pages/Settings';
import { CalibrationSettings } from './pages/CalibrationSettings';
import { TacticBriefsIndex, TacticBriefDetail } from './pages/TacticBriefs';
import { SetPieces } from './pages/SetPieces';
import { PrivacyNotice } from './pages/legal/PrivacyNotice';
import { PrivacyForYoungPlayers } from './pages/legal/PrivacyForYoungPlayers';
import { ParentShell } from './components/parent/ParentShell';
import { ParentOnlyRoute } from './components/parent/ParentOnlyRoute';
import { AdminOnlyRoute } from './components/AdminOnlyRoute';
import { CoachReview } from './pages/CoachReview';
import { CoachReviewWelcome } from './pages/CoachReviewWelcome';
const CoachInvites = lazy(() =>
  import('./pages/admin/CoachInvites').then((m) => ({ default: m.CoachInvites })),
);
const CoachReviewFeed = lazy(() =>
  import('./pages/admin/CoachReview').then((m) => ({ default: m.CoachReview })),
);
import { ParentHome } from './pages/parent/Home';
import { ParentResources } from './pages/parent/Resources';
import { ParentSettings } from './pages/parent/Settings';
import { ChildSetup } from './pages/parent/child/Setup';
import { ChildDetail } from './pages/parent/child/Detail';
import { ChildHighlights } from './pages/parent/child/Highlights';
import { ChildAiNote } from './pages/parent/child/AiNote';
import { DobScreen } from './pages/parent/consent/DobScreen';
import { ParentEmail } from './pages/parent/consent/ParentEmail';
import { PendingInvite } from './pages/parent/consent/PendingInvite';
import { PrivacyReview } from './pages/parent/consent/PrivacyReview';
import { YotiRedirect } from './pages/parent/consent/YotiRedirect';
import { GranularConsents } from './pages/parent/consent/GranularConsents';
import { ConsentConfirmation } from './pages/parent/consent/Confirmation';

export function App() {
  return (
    <Routes>
      {/* Public legal routes — no AppShell, no auth required */}
      <Route path="legal/privacy" element={<PrivacyNotice />} />
      <Route path="legal/privacy-for-young-players" element={<PrivacyForYoungPlayers />} />

      {/* Pro-coach review redemption — no shell, no auth required at the
          route level. The page itself calls the coach-review-redeem edge
          function which signs the coach in and redirects to /sessions/new. */}
      <Route path="coach-review/:token" element={<CoachReview />} />

      {/* Parental consent flow Steps 1–7 (Wave 3.2 + 3.3) — outside the
          parent shell + outside AuthGate (parent has no account yet during
          these steps). */}
      <Route path="parent/consent/dob" element={<DobScreen />} />
      <Route path="parent/consent/parent-email" element={<ParentEmail />} />
      <Route path="parent/consent/pending" element={<PendingInvite />} />
      <Route path="parent/consent/review" element={<PrivacyReview />} />
      <Route path="parent/consent/yoti" element={<YotiRedirect />} />
      <Route path="parent/consent/consents" element={<GranularConsents />} />
      <Route path="parent/consent/done" element={<ConsentConfirmation />} />

      {/* Parent portal — own shell, role-gated. Wave 3.1 home + resources,
          Wave 3.4 child detail, Wave 3.5 settings, plus the v1 roster
          surface from the v1 launch storyline (#89/#92/#93). */}
      <Route
        path="parent"
        element={
          <ParentOnlyRoute>
            <ParentShell />
          </ParentOnlyRoute>
        }
      >
        <Route index element={<ParentHome />} />
        <Route path="resources" element={<ParentResources />} />
        <Route path="settings" element={<ParentSettings />} />
        <Route path="child/setup" element={<ChildSetup />} />
        <Route path="child/:id" element={<ChildDetail />} />
        <Route path="child/:id/highlights" element={<ChildHighlights />} />
        <Route path="child/:id/ai-note" element={<ChildAiNote />} />
      </Route>

      <Route path="onboarding" element={<Onboarding />} />
      {/* Ben's first-run tour entry point. Auth-required (the magic-link
          redemption authenticates first). The component itself decides
          to render the tour or redirect to /sessions. */}
      <Route path="welcome" element={<CoachReviewWelcome />} />
      <Route path="reviews/check-in" element={<CoachGrowthCheckIn />} />
      <Route path="sessions/:id/run" element={<SessionRun />} />
      {/* Standalone full-bleed 3D surface — entered via "Open in 3D" on a session. */}
      <Route
        path="sessions/:id/3d"
        element={
          <Suspense fallback={null}>
            <SessionTactics3D />
          </Suspense>
        }
      />
      {/* SVG-based 2D counterpart — same persistKey as 3D so layouts sync. */}
      <Route
        path="sessions/:id/2d"
        element={
          <Suspense fallback={null}>
            <SessionTactics2D />
          </Suspense>
        }
      />
      {/* Phase 7 — coach-facing per-player tactical brief pages.
          Outside the AppShell so print-friendly + full-bleed. */}
      <Route path="tactics/:sessionId/briefs" element={<TacticBriefsIndex />} />
      <Route
        path="tactics/:sessionId/briefs/:playerKey"
        element={<TacticBriefDetail />}
      />
      <Route element={<AppShell />}>
        <Route index element={<Home />} />
        <Route path="sessions" element={<Sessions />} />
        <Route path="sessions/new" element={<SessionNew />} />
        <Route path="sessions/:id" element={<SessionDetail />} />
        {/* Sidebar entry — keeps the shell so 3D Pitch is reachable from any tab. */}
        <Route
          path="3d"
          element={
            <Suspense fallback={null}>
              <SessionTactics3D />
            </Suspense>
          }
        />
        <Route
          path="2d"
          element={
            <Suspense fallback={null}>
              <SessionTactics2D />
            </Suspense>
          }
        />
        <Route path="drills" element={<Drills />} />
        <Route path="drills/:id" element={<DrillDetail />} />
        <Route path="drills/:id/canvas" element={<DrillCanvasPage />} />
        {/* /drills/new — Brain-driven drill authoring (academy-gated).
            The "+ New drill" button on /drills creates a blank row
            directly and routes to /drills/:id?edit=1 instead, where the
            simpler DrillCanvasEditor handles manual edit. */}
        <Route
          path="drills/new"
          element={
            <AcademyOnlyRoute fallback="/drills">
              <DrillNew />
            </AcademyOnlyRoute>
          }
        />
        <Route path="tactics" element={<Tactics />} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="players" element={<Players />} />
        <Route path="players/:id" element={<PlayerDetail />} />
        <Route path="principles" element={<Principles />} />
        <Route path="curriculum" element={<Curriculum />} />
        <Route path="season" element={<Season />} />
        <Route path="games" element={<Games />} />
        <Route path="games/:id" element={<GameDay />} />
        <Route path="setpieces" element={<SetPieces />} />
        <Route path="memories" element={<Memories />} />
        <Route path="clips" element={<Clips />} />
        <Route path="reviews" element={<Reviews />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/calibration" element={<CalibrationSettings />} />

        {/* Admin surfaces — SUPER_ADMIN only. CoachInvites issues review
            URLs; CoachReview shows the live feed of a coach's voice +
            feedback during their review session. */}
        <Route
          path="admin/coach-invites"
          element={
            <AdminOnlyRoute>
              <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading admin page…</div>}>
                <CoachInvites />
              </Suspense>
            </AdminOnlyRoute>
          }
        />
        <Route
          path="admin/coach-review/:coachId"
          element={
            <AdminOnlyRoute>
              <Suspense fallback={<div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading review feed…</div>}>
                <CoachReviewFeed />
              </Suspense>
            </AdminOnlyRoute>
          }
        />
      </Route>
    </Routes>
  );
}
